import { Link, useLocation } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar'

export default function SidebarNav() {
  const location = useLocation()

  return (
    <>
      {/* ═══════════════════════════════════════════════════════════════
          WORKSPACE - Main working area
          ═══════════════════════════════════════════════════════════════ */}
      <SidebarGroup>
        <SidebarGroupLabel>Workspace</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            {/* Dashboard - Main KPI/Analytics page */}
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Dashboard Gerencial" isActive={location.pathname === '/dashboard'} asChild>
                <Link to="/dashboard">
                  <span>📊</span>
                  <span>Dashboard</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {/* Diagramas - Collapsible menu with Canvas and Visual Flows */}
            <Collapsible
              defaultOpen={location.pathname === '/' || location.pathname.startsWith('/visual-flows')}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    tooltip="Diagramas - Canvas y flujos visuales"
                    isActive={location.pathname === '/' || location.pathname.startsWith('/visual-flows')}
                  >
                    <span>📐</span>
                    <span>Diagramas</span>
                    <ChevronRight className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={location.pathname === '/canvas'}
                      >
                        <Link to="/canvas">
                          <span>🧭</span>
                          <span>Canvas</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={location.pathname.startsWith('/visual-flows')}
                      >
                        <Link to="/visual-flows">
                          <span>📁</span>
                          <span>Visual Flows</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
            {/* Automations - Collapsible menu with Workflows and Executions */}
            <Collapsible
              defaultOpen={location.pathname.startsWith('/workflow-automation')}
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    tooltip="Automations - Motor de automatización"
                    isActive={location.pathname.startsWith('/workflow-automation')}
                  >
                    <span>⚡</span>
                    <span>Automations</span>
                    <ChevronRight className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={location.pathname === '/workflow-automation'}
                      >
                        <Link to="/workflow-automation">
                          <span>🔧</span>
                          <span>Workflows</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={location.pathname === '/workflow-automation/executions'}
                      >
                        <Link to="/workflow-automation/executions">
                          <span>📊</span>
                          <span>Ejecuciones</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Incident Management" isActive={location.pathname === '/incidents'} asChild>
                <Link to="/incidents">
                  <span>🚨</span>
                  <span>Incidents</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {/* Personas - Natural persons (contacts) */}
            <SidebarMenuItem>
              <SidebarMenuButton
                tooltip="Personas - Contactos externos"
                isActive={location.pathname === '/persons'}
                asChild
              >
                <Link to="/persons">
                  <span>👤</span>
                  <span>Personas</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {/* Compañías - Legal entities (accounts) */}
            <SidebarMenuItem>
              <SidebarMenuButton
                tooltip="Compañías - Empresas externas"
                isActive={location.pathname === '/companies'}
                asChild
              >
                <Link to="/companies">
                  <span>🏢</span>
                  <span>Compañías</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {/* PROJ-058: Documents - SharePoint-like document management */}
            <SidebarMenuItem>
              <SidebarMenuButton
                tooltip="Documentos - Gestión documental estilo SharePoint"
                isActive={location.pathname === '/documents'}
                asChild
              >
                <Link to="/documents">
                  <span>📄</span>
                  <span>Documentos</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {/* ═══════════════════════════════════════════════════════════════
          GOVERNANCE - Compliance
          ═══════════════════════════════════════════════════════════════ */}
      <SidebarGroup>
        <SidebarGroupLabel>Gobernanza</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Gestión de Cumplimiento" isActive={location.pathname === '/compliance'} asChild>
                <Link to="/compliance">
                  <span>🛡️</span>
                  <span>Compliance</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {/* PROJ-042: Data Classification */}
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Clasificación de Datos para Privacidad y Consentimiento" isActive={location.pathname === '/data-classification'} asChild>
                <Link to="/data-classification">
                  <span>🏷️</span>
                  <span>Clasificación de Datos</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {/* PROJ-042 Phase 2: Data Attributes */}
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Atributos de Datos (PII, Sensibles, Financieros, etc.)" isActive={location.pathname === '/data-attributes'} asChild>
                <Link to="/data-attributes">
                  <span>🔖</span>
                  <span>Atributos de Datos</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {/* ═══════════════════════════════════════════════════════════════
          ADMINISTRATION - System configuration and security
          ═══════════════════════════════════════════════════════════════ */}
      <SidebarGroup>
        <SidebarGroupLabel>Administración</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            {/* PROJ-086: Artifacts Module - Files for Email Attachments */}
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Artefactos - Archivos para adjuntar en emails" isActive={location.pathname === '/artifacts'} asChild>
                <Link to="/artifacts">
                  <span>📎</span>
                  <span>Artefactos</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {/* PROJ-005: Security Module */}
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Roles, Permisos y Seguridad" isActive={location.pathname === '/security'} asChild>
                <Link to="/security">
                  <span>🔐</span>
                  <span>Seguridad</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {/* PROJ-060: Security Audit - Collapsible menu */}
            <Collapsible
              defaultOpen={
                location.pathname === '/my-sessions' ||
                location.pathname === '/login-history' ||
                location.pathname === '/security-alerts' ||
                location.pathname === '/compliance-reports'
              }
              className="group/collapsible"
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    tooltip="Auditoría de Seguridad - Sesiones, alertas y reportes"
                    isActive={
                      location.pathname === '/my-sessions' ||
                      location.pathname === '/login-history' ||
                      location.pathname === '/security-alerts' ||
                      location.pathname === '/compliance-reports'
                    }
                  >
                    <span>🔍</span>
                    <span>Auditoría</span>
                    <ChevronRight className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={location.pathname === '/my-sessions'}
                      >
                        <Link to="/my-sessions">
                          <span>📱</span>
                          <span>Mis Sesiones</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={location.pathname === '/login-history'}
                      >
                        <Link to="/login-history">
                          <span>📜</span>
                          <span>Historial de Logins</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={location.pathname === '/security-alerts'}
                      >
                        <Link to="/security-alerts">
                          <span>🚨</span>
                          <span>Alertas de Seguridad</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        isActive={location.pathname === '/compliance-reports'}
                      >
                        <Link to="/compliance-reports">
                          <span>📊</span>
                          <span>Reportes Compliance</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Configuración del Sistema" isActive={location.pathname === '/system-settings'} asChild>
                <Link to="/system-settings">
                  <span>⚙️</span>
                  <span>Configuración</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Catálogo Visual de Datos Maestros" isActive={location.pathname === '/visual-catalog'} asChild>
                <Link to="/visual-catalog">
                  <span>📦</span>
                  <span>Catálogo Visual</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Clasificación de Industrias" isActive={location.pathname === '/industries'} asChild>
                <Link to="/industries">
                  <span>🏭</span>
                  <span>Industrias</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Usuarios y Colaboradores" isActive={location.pathname === '/users'} asChild>
                <Link to="/users">
                  <span>👥</span>
                  <span>Usuarios</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Estructura Organizacional" isActive={location.pathname === '/organization'} asChild>
                <Link to="/organization">
                  <span>🏛️</span>
                  <span>Organización</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {/* PROJ-057: Field Metadata System */}
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Metadatos de Campos y Entidades" isActive={location.pathname === '/field-metadata'} asChild>
                <Link to="/field-metadata">
                  <span>📋</span>
                  <span>Metadata</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {/* PROJ-058 Wave 03: Document Categories */}
            <SidebarMenuItem>
              <SidebarMenuButton tooltip="Categorías de Documentos" isActive={location.pathname === '/document-categories'} asChild>
                <Link to="/document-categories">
                  <span>📂</span>
                  <span>Categorías de Docs</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    </>
  )
}
